import React, { useEffect, useState } from 'react';
import {
  Button, Dialog, DialogActions, DialogContent, DialogTitle,
  FormControlLabel, MenuItem, Stack, Switch, TextField,
} from '@mui/material';
import iziToast from 'izitoast';

import { createGroup, getAcademicYears, updateGroup } from '../../actions/journal';
import { internalServerError } from '../../utils/consts/pathRoutes';
import useIsMobile, { mobileDialogProps } from '../../utils/useIsMobile';

const EMPTY = {
  type: 'РЯ',
  number: '',
  semester: 1,
  academic_year: '',
  capacity: 15,
  status: 'Набор',
  opened_at: '',
  is_distant: false,
  is_paid: false,
  comment: '',
};

const STATUSES = ['Набор', 'Активна', 'Завершена', 'Архив'];

// Левая колонка формы: тип, семестр и мест стоят друг под другом, и разная ширина
// у соседних по вертикали полей выглядит как съехавшая вёрстка.
const NARROW = { width: 120 };

/** Группа из ответа API → значения формы. Пустые поля приходят как null, полям ввода нужна строка. */
const fromGroup = (group) => ({
  type: group.type,
  number: group.number,
  semester: group.semester,
  academic_year: group.academic_year,
  capacity: group.capacity,
  status: group.status,
  opened_at: group.opened_at || '',
  is_distant: Boolean(group.is_distant),
  is_paid: Boolean(group.is_paid),
  comment: group.comment || '',
});

/**
 * Создание и правка группы.
 *
 * Одна форма на оба случая: поля совпадают, а разводить две почти одинаковые
 * означало бы чинить каждую правку дважды. Отличается заголовок, кнопка и то,
 * что у существующей группы можно сменить статус — новая всегда начинается с набора.
 */
const GroupModal = ({ open, group, defaultAcademicYear, onClose, onSaved }) => {
  const isMobile = useIsMobile();
  const isEdit = Boolean(group);

  const [form, setForm] = useState(EMPTY);
  const [years, setYears] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;

    if (isEdit) {
      setForm(fromGroup(group));
    } else {
      setForm({ ...EMPTY, academic_year: defaultAcademicYear || '' });
    }

    // Тот же список, что и в фильтре: год всегда промежуток в один год,
    // и вводить его руками незачем.
    getAcademicYears()
      .then((items) => {
        setYears(items);
        if (!isEdit && !defaultAcademicYear && items.length) {
          setForm((prev) => ({ ...prev, academic_year: items[0] }));
        }
      })
      .catch(() => setYears([]));
  }, [open, isEdit, group, defaultAcademicYear]);

  const change = (field) => (event) =>
    setForm((prev) => ({ ...prev, [field]: event.target.value }));

  const toggle = (field) => (event) =>
    setForm((prev) => ({ ...prev, [field]: event.target.checked }));

  const submit = () => {
    setSaving(true);

    const payload = { ...form, opened_at: form.opened_at || null };
    const request = isEdit ? updateGroup(group.id, payload) : createGroup(payload);

    request
      .then((saved) => {
        iziToast.success({
          message: isEdit ? `Группа ${saved.name} изменена` : `Группа ${saved.name} создана`,
          position: 'topRight',
        });
        onSaved(saved);
      })
      .catch((e) => iziToast.error({ message: internalServerError(e), position: 'topRight' }))
      .finally(() => setSaving(false));
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth {...mobileDialogProps(isMobile)}>
      <DialogTitle>{isEdit ? `Группа ${group.name}` : 'Новая группа'}</DialogTitle>

      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Stack direction="row" spacing={2}>
            <TextField select label="Тип" value={form.type} onChange={change('type')} sx={NARROW}>
              <MenuItem value="РЯ">РЯ</MenuItem>
              <MenuItem value="ПО">ПО</MenuItem>
            </TextField>
            <TextField
              label="Номер" type="number" value={form.number}
              onChange={change('number')} fullWidth
            />
          </Stack>

          <Stack direction="row" spacing={2}>
            <TextField select label="Семестр" value={form.semester} onChange={change('semester')} sx={NARROW}>
              <MenuItem value={1}>1</MenuItem>
              <MenuItem value={2}>2</MenuItem>
              <MenuItem value={3}>3</MenuItem>
            </TextField>
            <TextField
              select label="Учебный год" value={form.academic_year}
              onChange={change('academic_year')} fullWidth
            >
              {years.map((year) => (
                <MenuItem key={year} value={year}>{year}</MenuItem>
              ))}
            </TextField>
          </Stack>

          <Stack direction="row" spacing={2}>
            <TextField
              label="Мест" type="number" value={form.capacity}
              onChange={change('capacity')} sx={NARROW}
            />
            <TextField
              label="Дата открытия" type="date" value={form.opened_at}
              onChange={change('opened_at')} InputLabelProps={{ shrink: true }} fullWidth
            />
          </Stack>

          {isEdit && (
            <TextField select label="Статус" value={form.status} onChange={change('status')}>
              {STATUSES.map((status) => <MenuItem key={status} value={status}>{status}</MenuItem>)}
            </TextField>
          )}

          {/* Два переключателя с подписями шире телефонного диалога — там они в столбик. */}
          <Stack direction={isMobile ? 'column' : 'row'} spacing={isMobile ? 0 : 2}>
            <FormControlLabel
              control={<Switch checked={form.is_distant} onChange={toggle('is_distant')} />}
              label="Дистанционная"
            />
            <FormControlLabel
              control={<Switch checked={form.is_paid} onChange={toggle('is_paid')} />}
              label="Платная"
            />
          </Stack>

          <TextField
            label="Комментарий" value={form.comment}
            onChange={change('comment')} multiline rows={2}
          />
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Отмена</Button>
        <Button variant="contained" onClick={submit} disabled={saving || !form.number}>
          {isEdit ? 'Сохранить' : 'Создать'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default GroupModal;
